/* eslint-disable camelcase */
import api from '../../http/api/api.js';
import constants from '../../../constant';

/*
 * castStatus module
 *
 * Centralised polling loop for the "is this cast currently live?" flag.
 * One loop runs per authenticated session, regardless of how many card
 * views are mounted - list pages push cast IDs into a shared set on
 * mount and the same interval batches them into a single backend call.
 *
 * Why this shape:
 * - Backend hop is batched (one HTTP request per tick, not per card),
 *   so rendering 30 casts costs one round-trip every POLL_INTERVAL_MS.
 * - The tick is paused when the tab is hidden (Page Visibility API) and
 *   forced on re-focus, so a backgrounded tab doesn't keep hammering.
 * - Rapid registrations (e.g. user opens a list with many casts) are
 *   collapsed via MIN_FETCH_GAP_MS so the loop can't degenerate into a
 *   burst of requests.
 * - When the user actually starts a cast, `registerCast` both adds the
 *   ID to the polling set AND force-fires an immediate fetch so the
 *   LIVE badge flips on without waiting up to POLL_INTERVAL_MS.
 */

// How often to re-check the statuses registered in the polling set.
// 10 seconds is a decent compromise - responsive enough that a live
// stream appears on the dashboard within ~10s of RTMP connecting, slow
// enough that even 50 casts = 1 req / 10s = trivial load.
const POLL_INTERVAL_MS = 10000;

// Minimum wall-clock gap between two fetch calls. Protects against
// stampedes when multiple list views mount in quick succession (e.g.
// tab switch rapidly registers the same IDs).
const MIN_FETCH_GAP_MS = 2000;

// Module-scoped handles for the interval + visibility listener. Kept
// outside Vuex state on purpose - Vuex state should be serialisable
// and a setInterval handle is neither serialisable nor meaningful to
// persist. Storing them here means `stopPolling` can find and clear
// them regardless of how many times start/stop get called.
let pollingTimer = null;
let visibilityHandler = null;

const getDefaultState = () => ({
  // Map of public_meeting_id → boolean (true = currently live)
  statuses: {},
  // Set of public_meeting_ids that the loop should check each tick.
  // Stored as an object so Vue's reactivity can track membership
  // changes; a plain Set would not trigger reactivity on add/delete.
  registeredIds: {},
  // Epoch-ms of the last fetch attempt. Used by the throttle in
  // `fetchStatuses` below.
  lastFetchAt: 0,
});

const mutations = {
  SET_STATUSES(state, next) {
    state.statuses = { ...state.statuses, ...next };
  },
  ADD_REGISTERED(state, ids) {
    const next = { ...state.registeredIds };
    ids.forEach((id) => {
      if (id) next[id] = true;
    });
    state.registeredIds = next;
  },
  REMOVE_REGISTERED(state, ids) {
    const next = { ...state.registeredIds };
    ids.forEach((id) => {
      delete next[id];
    });
    state.registeredIds = next;
  },
  CLEAR_REGISTERED(state) {
    state.registeredIds = {};
  },
  SET_LAST_FETCH_AT(state, ts) {
    state.lastFetchAt = ts;
  },
  RESET(state) {
    Object.assign(state, getDefaultState());
  },
};

const actions = {
  // Hits the backend proxy with every currently-registered cast ID and
  // merges the response into state. Silently bails on network errors
  // so a transient failure doesn't show stale casts as offline - the
  // next tick will just try again.
  async fetchStatuses({ state, commit }) {
    const ids = Object.keys(state.registeredIds);
    if (ids.length === 0) return;

    const now = Date.now();
    if (now - state.lastFetchAt < MIN_FETCH_GAP_MS) return;
    commit('SET_LAST_FETCH_AT', now);

    try {
      const res = await api.post(
        `${constants.apiCastUrl}/api/event/stream/status/`,
        { public_meeting_ids: ids }
      );
      const payload = res && res.data;
      if (!payload || !payload.status || !payload.statuses) return;
      const next = {};
      Object.keys(payload.statuses).forEach((pmid) => {
        const entry = payload.statuses[pmid];
        next[pmid] = !!(entry && entry.live);
      });
      commit('SET_STATUSES', next);
    } catch (e) {
      // Best-effort - don't spam the console on every failed tick.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('castStatus fetch failed:', e && e.message);
      }
    }
  },

  // Registers a list of cast IDs and ensures the background interval
  // is running. Idempotent - safe to call repeatedly from mounted()
  // hooks as lists rerender. Fires an immediate fetch so the LIVE
  // badges appear without waiting for the first interval tick.
  startPolling({ state, commit, dispatch }, { castIds = [] } = {}) {
    commit('ADD_REGISTERED', castIds);
    dispatch('fetchStatuses');

    if (pollingTimer) return;

    pollingTimer = setInterval(() => {
      // Skip ticks while the tab is hidden. Combined with the
      // visibility listener below, this means a backgrounded tab
      // stops polling entirely until the user comes back.
      if (typeof document !== 'undefined' && document.hidden) return;
      dispatch('fetchStatuses');
    }, POLL_INTERVAL_MS);

    if (typeof document !== 'undefined') {
      visibilityHandler = () => {
        if (!document.hidden) dispatch('fetchStatuses');
      };
      document.addEventListener('visibilitychange', visibilityHandler);
    }
  },

  // Removes cast IDs from the polling set. If no IDs are left, the
  // interval keeps running but the guard in `fetchStatuses` short-
  // circuits so no HTTP request is made. Callers that are tearing
  // down completely (dashboard unmount, logout) should use
  // `stopPolling` instead to actually clear the interval.
  unregisterCasts({ commit }, castIds = []) {
    commit('REMOVE_REGISTERED', castIds);
  },

  // Force-register a single cast and immediately re-check it.
  // Used when the user clicks "Join" / "Start" - the cast was
  // probably not in the registered set before (fresh streams start
  // with is_streaming=false in the card data), and we want the LIVE
  // badge to flip as soon as the RTMP connection comes up.
  registerCast({ commit, dispatch }, castId) {
    if (!castId) return;
    commit('ADD_REGISTERED', [castId]);
    dispatch('fetchStatuses');
  },

  // Optimistically set a cast's live state without waiting for the
  // next poll tick. Used right after the user's start/stop action
  // succeeds so the badge + button flip instantly instead of lagging
  // up to POLL_INTERVAL_MS behind ground truth. The next natural
  // poll tick will reconcile with the upstream reality regardless.
  setStatus({ commit }, { publicMeetingId, live }) {
    if (!publicMeetingId) return;
    commit('SET_STATUSES', { [publicMeetingId]: !!live });
  },

  // Tear everything down. Called from list pages' beforeDestroy.
  // Safe to call multiple times - all the nulling is guarded.
  stopPolling({ commit }) {
    if (pollingTimer) {
      clearInterval(pollingTimer);
      pollingTimer = null;
    }
    if (visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
    }
    commit('RESET');
  },
};

const getters = {
  // Returns a function so templates can write
  //   v-if="$store.getters['castStatus/isLive'](cast.public_meeting_id)"
  // without plumbing the full statuses map through each card.
  isLive: (state) => (publicMeetingId) => !!state.statuses[publicMeetingId],
};

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations,
  actions,
  getters,
};
